<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Renders tenant-authored HTML — pasted inline or uploaded as a file —
 * inside a sandboxed iframe. `allow-scripts` is set without
 * `allow-same-origin`, so the content can run its own scripts but gets an
 * opaque origin: it cannot read this page's cookies, localStorage, or DOM.
 *
 * This is deliberately different from EmbedBlock, whose provider allowlist
 * grants `allow-same-origin` because the frame lands on the *provider's*
 * origin, not ours. Here the content can be anything a tenant wrote, so it
 * must never share our origin.
 *
 * `data.html` is intentionally absent from GenericBlocks::HTML_FIELDS /
 * server-side purification — the whole point of this block is to preserve
 * scripts, so the sandbox (not content stripping) is the safety boundary.
 *
 * Auto height: the sandbox hides the frame's document from us, so the content
 * reports its own height. Pasted HTML gets the reporter appended; an uploaded
 * file must include this in a script tag before the closing body tag (tags
 * omitted here — a literal closing script tag would end this SFC block):
 *
 *   (function(){function s(){parent.postMessage({type:'zth-html-height',height:document.documentElement.scrollHeight},'*')}
 *   addEventListener('load',s);new ResizeObserver(s).observe(document.documentElement)})();
 *
 * Avoid 100vh / min-height:100% on the root — the frame's height then feeds
 * its content's height and the two grow together until the clamp.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const SANDBOX = 'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms'
const MIN_HEIGHT = 100
const MAX_HEIGHT = 20000

const HEIGHT_REPORTER = '<scr' + 'ipt>(function(){function s(){parent.postMessage({type:\'zth-html-height\',height:document.documentElement.scrollHeight},\'*\')}addEventListener(\'load\',s);new ResizeObserver(s).observe(document.documentElement)})();<\/script>'

const frame = ref<HTMLIFrameElement | null>(null)
// `data.height` survives only on blocks saved before auto height existed.
const reported = ref<number | null>(null)
const height = computed(() => `${reported.value ?? props.data.height ?? 600}px`)

const srcdoc = computed(() => props.data.html ? `${props.data.html}${HEIGHT_REPORTER}` : '')

const onMessage = (event: MessageEvent) => {
  // Opaque-origin frames post with origin "null", so identity is checked by
  // window, not origin — other frames on the page can't resize this one.
  if (!frame.value || event.source !== frame.value.contentWindow) return
  const msg = event.data as { type?: unknown, height?: unknown }
  if (msg?.type !== 'zth-html-height' || typeof msg.height !== 'number' || !Number.isFinite(msg.height)) return
  // +2 covers the iframe's 1px border on each side.
  reported.value = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.ceil(msg.height) + 2))
}

onMounted(() => window.addEventListener('message', onMessage))
onBeforeUnmount(() => window.removeEventListener('message', onMessage))
</script>

<template>
  <iframe
    v-if="data.url"
    ref="frame"
    :src="data.url"
    :sandbox="SANDBOX"
    title="Custom HTML"
    :style="{ height }"
    class="w-full rounded-[var(--site-radius)] border border-[var(--site-border)]"
    loading="lazy"
    referrerpolicy="no-referrer"
  />
  <iframe
    v-else-if="data.html"
    ref="frame"
    :srcdoc="srcdoc"
    :sandbox="SANDBOX"
    title="Custom HTML"
    :style="{ height }"
    class="w-full rounded-[var(--site-radius)] border border-[var(--site-border)]"
    loading="lazy"
    referrerpolicy="no-referrer"
  />
  <div
    v-else
    class="rounded-[var(--site-radius)] border border-dashed border-[var(--site-border)] p-8 text-center text-sm text-[var(--site-muted)]"
  >
    Paste HTML or upload a file to display it here.
  </div>
</template>
